import React from "react";
import LegalLayout from "../../layouts/LegalLayout/LegalLayout";
import styles from "./Legal.module.scss";

const CardTermsInternational = () => {
  return (
    <LegalLayout title="STBL CARD TERMS (INTERNATIONAL)">
      <div className={styles.legalContent}>
        <section className={styles.section}>
          <h2>STBL SPEND CARD TERMS</h2>
          <p>
            These STBL Spend Card Terms (the &ldquo;Card Terms&rdquo;) are a
            binding agreement between you (&ldquo;you&rdquo; or
            &ldquo;your&rdquo;) and the Issuer (&ldquo;we&rdquo;,
            &ldquo;us&rdquo;, or &ldquo;our&rdquo;) that governs your use of the
            STBL Spend Cards, including the process for obtaining and managing
            STBL Spend Cards, access to which is provided to you by STBL, LLC
            (&ldquo;STBL&rdquo;).
          </p>

          <h3>Important Disclosures</h3>
          <div className={styles.importantNote}>
            <p>
              PLEASE REVIEW THE ARBITRATION CLAUSE AND NOTICES SET FORTH BELOW
              IN SECTION 16. BY USING THE CARD, YOU ARE AGREEING TO THE
              ARBITRATION CLAUSE AND NOTICES SET FORTH IN THAT SECTION. THE
              ARBITRATION CLAUSE WILL HAVE A SUBSTANTIAL EFFECT ON YOUR RIGHTS
              IN THE EVENT OF A DISPUTE, INCLUDING YOUR RIGHT TO BRING OR
              PARTICIPATE IN A CLASS PROCEEDING.
            </p>
          </div>
          <p>
            Rates, fees, and other important information about your STBL Spend
            Card (&ldquo;STBL Card&rdquo; or &ldquo;Card&rdquo;) are set forth in
            these Important Disclosures.
          </p>
          <p className={styles.lastUpdated}>Effective as of 9/15/2026</p>

          <h3>Interest Rates and Interest Charges</h3>
          <table className={styles.disclosureTable}>
            <thead>
              <tr>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={styles.labelCell}>
                  Interest Rates and Interest Charges
                </td>
                <td>0%</td>
              </tr>
              <tr>
                <td className={styles.labelCell}>
                  Annual Percentage Rate (APR) for Purchases
                </td>
                <td>0%</td>
              </tr>
            </tbody>
          </table>
          <p>
            Your STBL Spend Card is currently 0% interest on all purchases.
            Issuer and STBL reserve the right to implement interest in the
            future, for new purchases. STBL will disclose any changes to this
            agreement prior to the introduction of interest and other charges
            associated with your STBL Card.
          </p>

          <h3>Fees</h3>
          <h4>Transaction Fee</h4>
          <table className={styles.disclosureTable}>
            <thead>
              <tr>
                <th>Fee Type</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={styles.labelCell}>Foreign Purchases</td>
                <td>
                  <ul>
                    <li>Foreign Exchange Fee (non USD): up to 3%</li>
                    <li>Cross Border Fee: up to 3%</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>

          <h4>Penalty Fees</h4>
          <table className={styles.disclosureTable}>
            <thead>
              <tr>
                <th>Fee Type</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={styles.labelCell}>Late payment</td>
                <td>Up to $40</td>
              </tr>
              <tr>
                <td className={styles.labelCell}>Returned payment</td>
                <td>Up to $29</td>
              </tr>
            </tbody>
          </table>

          <div className={styles.importantNote}>
            <p>
              WHEN YOU APPLY FOR A CARD ACCOUNT, ACTIVATE A CARD, OR OTHERWISE
              PARTICIPATE IN THE PROGRAM IN ANY WAY, YOU REPRESENT THAT YOU HAVE
              READ, UNDERSTAND, AND AGREE TO THESE CARD TERMS.
            </p>
          </div>
        </section>

        <div className={styles.divider} />

        <section className={styles.section}>
          <h2>Terms</h2>

          <h3>Background</h3>
          <p>
            The STBL Card is provided to you on behalf of STBL in connection with
            your status as STBL customer and pursuant to your separate User
            Agreement between you and STBL (the &ldquo;User Agreement&rdquo;).
            The Issuer is not a party to the User Agreement and disclaims any
            liability for the performance of services covered therein.
          </p>
          <p>
            STBL has opened a STBL Account for purposes of facilitating
            transactions you make using a Card based on a limit established by
            STBL pursuant to the User Agreement. You understand that you have
            access to the Services and STBL Card only to the extent authorized by
            the STBL. You acknowledge and agree that STBL will satisfy
            obligations created through your use of the STBL Card and you will
            repay STBL based on the terms of your User Agreement, subject to the
            terms below. In the event of a conflict between these Card Terms and
            your User Agreement, these Card Terms will control.
          </p>
          <p>
            Details on STBL&rsquo;s collection, use, and handling of your
            personal data are described in our Privacy Policy. Please review it
            carefully and contact STBL if you have any questions. By opening,
            using, or maintaining a Card, you consent to and direct Issuer to
            share information relating to transactions, including receipt
            information or other personal data, in order to deliver the
            Services.
          </p>

          <h3>Issuer</h3>
          <p>
            The card program is issued by the Issuer under license from Visa.
            The information about the cost of the Card described in the above
            table is accurate as of 9/15/2026. This information may change after
            that date. To find out what may have changed, call or write the
            servicer, at{" "}
            <a href="mailto:support@stbl.io">support@stbl.io</a> and +1 (385)
            600-7758.
          </p>
          <p>
            Issuer reserves the right to amend these Card Terms or impose
            additional obligations or restrictions on you at any time with or
            without notice to you. By continuing to use the Services, you agree
            to be bound by such amendments or additional obligations or
            restrictions. The date on the top of this page shows when the Card
            Terms were last updated. Capitalized terms that are not defined here
            have the definitions provided in the User Agreement.
          </p>
          <p>
            STBL provides technology services in connection with the STBL Card,
            and is not a bank, credit union, or money services business and does
            not itself extend credit, set interest rates, determine repayment
            terms or hold Collateral. Nothing in these Card Terms shall be
            construed as creating a lender-borrower relationship between you and
            STBL.
          </p>
          <p>You acknowledge and agree that STBL:</p>
          <ul>
            <li>
              (i) is an express third-party beneficiary of these Card Terms,
              with the limited right to enforce obligations that directly relate
              to its role;
            </li>
            <li>
              (ii) is not responsible for any decisions by the Issuer to
              approve, decline, suspend or close your Card account. STBL may, at
              the request of the Issuer, the payment network or a regulatory
              authority, suspend or restrict your access to the STBL platform or
              certain features, to protect against fraud, comply with applicable
              laws or manage program risk;
            </li>
            <li>
              (iii) does not control and is not responsible for the operation,
              security, or performance of the smart contracts used to hold your
              Collateral; and
            </li>
            <li>
              (iv) is not responsible for merchant acceptance of your STBL Card
              or for resolving disputes about goods or services purchased with
              the STBL Card.
            </li>
          </ul>

          <h3>Defined Terms</h3>
          <p>
            &ldquo;Card Networks&rdquo; means the payment card networks
            including Visa or Mastercard.
          </p>
          <p>
            &ldquo;Charge&rdquo; means a payment for goods or services made to a
            merchant that accepts payments on the applicable Card Network.
          </p>
          <p>
            &ldquo;Chargeback&rdquo; means a dispute that you initiate against a
            merchant for an unresolved dispute with the merchant or where a
            Charge is unauthorized.
          </p>
          <p>
            &ldquo;Fee&rdquo; means charges we impose on you for use of Services
            or your use of a STBL Card.
          </p>
          <p>
            &ldquo;Issuer&rdquo; refers to Third National, including its
            affiliates, successors, and assigns.
          </p>
          <p>
            &ldquo;Periodic Statement&rdquo; means the periodic statements that
            reflect activity for all Cards issued to you identifying charges,
            fees, refunds, or other amounts owed or credited to your STBL
            Account during the time covered by that statement.
          </p>
          <p>
            &ldquo;Supported Blockchains&rdquo; means the Ethereum Blockchain,
            Polygon Blockchain, Optimism Blockchain, Arbitrium Blockchain and
            other blockchain networks which may be added at the sole discretion
            of Issuer. We may update this list of Supported Blockchains at any
            time and at our sole discretion.
          </p>

          <h3>Agreement</h3>

          <h4>1. Accepting this Agreement &amp; Eligibility</h4>
          <p>
            These Card Terms become effective and legally binding when you
            activate or create your Card by following the instructions on the
            STBL platform. You and we agree to comply with, and be bound by,
            this entire agreement. You should retain and carefully review these
            Card Terms. By creating a Card, you agree to the Arbitration Clause
            below as it pertains to these Card Terms, even if you do not use the
            Account or the Card.
          </p>
          <p>
            By using a Card you represent and warrant in your individual
            capacity that:
          </p>
          <ul>
            <li>
              You are not a person who is blocked or sanctioned by the United
              States Government, including those identified by the United States
              Office of Foreign Asset Controls (OFAC).
            </li>
            <li>
              You will use the Services exclusively for purposes permitted by
              these Card Terms.
            </li>
            <li>
              All information you provide to us, either directly or through
              STBL, is and will be true, correct, and complete.
            </li>
            <li>
              You will use the STBL Card for personal, family, or household use.
            </li>
            <li>
              You will only use the STBL Card in compliance with applicable law
            </li>
            <li>
              You attest that you are not a United States citizen, and that you
              are signing up for a card that is intended for those outside of
              the United States.
            </li>
            <li>You attest that you were not solicited for this Card.</li>
          </ul>

          <h4>2. Issuer Terms</h4>
          <p>
            The Issuer is identified on the back of the STBL Card issued to you
            and is responsible for funding your payments for goods and services
            you purchase at a merchant through your STBL Card and based on
            information provided by Partner. Please note that the Issuer may
            require you to accept additional terms in addition to the agreements
            you have with STBL, and your use of the STBL Cards will then also be
            subject to such additional terms.
          </p>

          <h4>3. Collateral</h4>
          <p>
            This Account is a secured Account. Either your primary Linked Wallet
            or any Additional Wallets may provide the collateral that will
            secure the Charges made by you on any Card (the
            &ldquo;Collateral&rdquo;). The Collateral must be held in a wallet
            on a Supported Blockchain. By entering into these Card Terms, you
            are furnishing and granting us a security interest in the
            Collateral, as well as any additions to, substitutions or renewals
            of the Collateral. No portion of the Collateral may be used by you
            to secure other loans.
          </p>
          <p>
            A &ldquo;Liquidation Event&rdquo; will occur a) if you have an
            outstanding payment obligation to STBL or the Issuer, as applicable,
            and such payment obligation has not been paid in full by you within
            one (1) calendar day; OR b) the Market Value of your Collateral
            drops below the value of the existing charges on your STBL Card(s)
            and you do not add additional collateral or reduce the value of the
            existing charges to less than the Market Value of the Collateral by
            making a payment. STBL and Issuer may allow for an additional grace
            period at their discretion.
          </p>
          <p>
            &ldquo;Market Value&rdquo; of the Collateral will be determined by
            STBL and/or Issuer using the net redemption value provided by a
            centralized stablecoin issuer and/or the real time price posted on a
            reputable and recognized exchange or price aggregator, or by
            reference to a price oracle, subject to STBL or Issuers discretion.
            You agree to pay all transaction costs or &ldquo;gas&rdquo; fees
            relating to any Liquidation transaction and that all such costs or
            fees will be paid from your Collateral and will not be credited to
            your Account. You agree that the market value of your collateral is
            determined solely by Issuer through the above methods. You further
            agree that the Market Value is determined at the time of a
            Liquidation Event and any change in or fluctuation in value of the
            Collateral before or after a Liquidation Event will have no bearing
            on obligations owed to STBL and/or Issuer.
          </p>
          <p>
            You, through one or more of your Linked Wallet or Additional
            Wallets, must ensure that the Collateral has a Market Value in
            United States Dollars (&ldquo;USD&rdquo;) that is greater than or
            equal to the value of all unpaid Charges to all of your STBL Cards.
            For example, if you provide Digital Assets with a Market Value equal
            to $100 of value in USD, and that Market Value does not change, your
            spending limit will be equal to $100 USD across all your STBL Cards.
            Once $100 USD has been charged to your STBL Cards, you will be
            required to either make a payment or to provide additional
            Collateral in order to make any additional Charges. If, at any time,
            the Market Value of your Collateral becomes less than the value of
            all unpaid Charges, we may require you to add to the Collateral or
            you may be subject to a Liquidation Event, at our discretion. If the
            Market Value of your Collateral is subject to a Liquidation Event
            when the Market Value is below your existing charges, you still owe
            STBL and Issuer any difference between the USD value of the
            charges, and the USD value of the collateral at the time of the
            Liquidation Event.
          </p>
          <p>
            Your withdrawal of any Collateral will not terminate any outstanding
            payment obligations you may have on your STBL Cards.
          </p>
          <p>
            STBL and Issuer will not, in any circumstance, be holding custody of
            your Collateral. Issuer is not a custodian or owner of your
            Collateral. You authorize and consent to STBL or Issuer liquidating
            the Collateral upon a Liquidation Event through a third party or by
            other means in order to satisfy payment obligations owed by you to
            STBL, the Issuer or other third party, as applicable.
          </p>
          <p>
            You acknowledge and agree that the Supported Blockchains, including
            any Supported Blockchains that consist of or include tokenized
            assets, are issued and managed by third parties, and are subject to
            their own terms, conditions, and risks. We and STBL make no
            representations or warranties regarding any Supported Blockchain, or
            any underlying Collateral related to such Supported Blockchain,
            including but not limited to their value, liquidity, stability,
            yield, rewards, or any other attributes. We and STBL expressly
            disclaim all responsibility and liability for any losses,
            fluctuations, or other outcomes arising from the ownership, use,
            performance, or characteristics of any Supported Blockchain and/or
            Collateral. You are responsible for evaluating and accepting the
            risks associated with any Supported Blockchain you use to provide
            Collateral.
          </p>
          <p>
            You waive any right to require us to: (a) proceed against any other
            person; (b) proceed against or exhaust any security held under these
            Card Terms; or (c) pursue any other remedy available to us under
            applicable law. We may, without prior notice, and from time to time:
            (a) renew, compromise, extend, accelerate or otherwise change the
            terms relating to the Account; (b) take and hold security (other
            than the Collateral) for payment of the Account and enforce,
            exchange and release the security in any manner that we determine
            is proper; (c) release or substitute any person, guarantor, or
            endorser of the Account; and (d) increase or lower the Credit Limit
            on your Account, and no such action shall change the fact that the
            Collateral shall at all times serve as security for the Account.
          </p>

          <h4>4. Spending Limits</h4>
          <p>
            Your spending limit is generally set by STBL pursuant to the terms
            of the STBL Terms and the amount of Collateral. Issuer may
            additionally set spending limits on each STBL Card or an aggregate
            spending limit across all STBL Cards, at its sole discretion. STBL
            Account Spending limits are dynamic and may be modified at any time
            with or without notice to you, including temporary increases or
            decreases or reducing spending limits to $0. Any authorized Charge
            or fee on a STBL Card may reduce your spending limit by a
            corresponding amount.
          </p>
          <p>
            When you use your STBL Card to initiate a transaction at certain
            merchants where the amount of the final transaction is unknown at
            the time of authorization, Issuer may assess a value to such a
            Charge for an amount higher or lower than the final Charges. This
            initial &ldquo;hold&rdquo; Charge will reduce your spending limit
            until the final Charge is determined.
          </p>

          <h4>5. Purchases &amp; Restrictions</h4>
          <p>
            STBL and Issuer reserve the right to block and terminate
            transactions and suspend access to your Account, unless prohibited
            by applicable law, at any time and for any reason, including if we
            believe that you are using the Card or your Account for
            non-consumer purposes. You may use your Account to purchase or lease
            goods or services (each, a &ldquo;Purchase&rdquo;) by presenting
            your Card or providing to participating merchants and establishments
            that honor the Card your Card number and additional information by
            any other means (for example, over the phone, online or through a
            mobile app). We will not be liable to you (or anyone else) if any
            merchant or other person cannot or will not process a purchase
            permitted under these Card Terms. Unless prohibited by applicable
            law, we may from time to time limit the type, number and dollar
            amount of any purchase, including any cash-like transaction, even if
            you have sufficient available credit to complete the purchase.
            Cash-like transactions include, but are not limited to, the
            following transactions to the extent they are accepted:
          </p>
          <ul>
            <li>
              purchasing travelers checks, foreign currency, money orders, wire
              transfers, cryptocurrency, other similar digital or virtual
              currency and other similar transactions;
            </li>
            <li>
              purchasing lottery tickets, casino gaming chips, race track
              wagers, and similar offline and online betting transactions;
            </li>
            <li>
              person-to-person money transfers and account-funding transactions
              that transfer currency; and
            </li>
            <li>
              making a payment using a third-party service including bill
              payment transactions not made directly with the merchant or their
              service provider.
            </li>
          </ul>
          <p>
            We reserve the right to deny any purchase for any reason, such as
            account default, suspected fraudulent or unlawful activity, internet
            gambling, or any indication of increased risk related to the
            purchase. We may terminate or suspend your use of the Card or the
            Account, with or without notice to you before or at the time we take
            such action. Cash advances and balance transfers are not available
            under these Card Terms.
          </p>
          <p>
            You acknowledge and agree that you have read and understood the{" "}
            <a
              href="https://legal.raincards.xyz/legal/prohibitions"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://legal.raincards.xyz/legal/prohibitions
            </a>{" "}
            and that you will not engage in any such activities when using the
            Services or the STBL Card.
          </p>
          <p>
            You acknowledge and agree that you will not use the STBL Card (1)
            for any expense which is not an expense incurred by you; (2) for any
            purpose prohibited by these Card Terms; or (3) for, with, or for the
            benefit of any individual or entity who is blocked or sanctioned by
            the United States, including those identified by the United States
            Department of Treasury&rsquo;s Office of Foreign Assets Control
            (OFAC).
          </p>
          <p>
            You will use all reasonable means to protect your STBL Cards and
            log-in credentials to the STBL Account from unauthorized use. You
            will not allow any other person or third party to use the Services
            or the STBL Card on your behalf. You will immediately notify us
            where you know or suspect that access to your STBL Account has been
            compromised or your STBL Card has been lost, stolen, or compromised
            in any way.
          </p>

          <h4>6. Payments</h4>
          <h4>6.1 Promise to Pay</h4>
          <p>
            While you will generally repay STBL for amounts transacted with your
            Card, you also promise to pay Issuer or its assignees for all
            amounts charged to the Account not repaid to Issuer by Partner,
            including all purchases, interest, and charges charged to your
            Account. Except as prohibited by applicable law, you are obligated
            to repay STBL or its assignees, for all transactions made using
            your Card by people you have authorized to use the Card even if
            their use of the Card exceeds the authorization which you gave them,
          </p>

          <h4>6.2 Periodic Statements</h4>
          <p>
            You are responsible for payment in full of all Charges and Fees.
            Your STBL Account may furnish to you Periodic Statements identifying
            Charges, Fees, refunds, the amount of your Collateral, any other
            Card transactions, or other amounts owed or credited to your STBL
            Account. Periodic Statements may be made accessible to you on your
            STBL Account daily, monthly, or as otherwise prescribed by Issuer.
            You must notify us promptly if you believe that there are any errors
            on your Periodic Statement, and submit any disputes or Chargebacks
            in accordance with these Card Terms. We are not obligated to send
            you a monthly statement if we deem your Account to be uncollectible
            or applicable law does not require us to send you a monthly
            statement for other reasons. Your failure to get a statement will
            have no bearing on your obligations and STBL and Issuer may still
            liquidate your collateral per Section 3 of this agreement.
          </p>

          <h4>6.3 Repayment</h4>
          <p>
            Where applicable, you may make a repayment for a balance on one or
            more of your STBL Cards by any means that are permitted by STBL and
            as provided in your STBL Account.
          </p>
          <p>
            Any failure to pay the full amount owed to STBL or the Issuer, as
            applicable, when required is a breach of these Card Terms. You are
            responsible for all costs or expenses that we may incur in
            collecting amounts owed but not timely paid, including legal or
            collections fees and any interest at the maximum rate permitted
            under law.
          </p>

          <h4>6.4 Prepayment</h4>
          <p>
            At any time and where applicable, you may pay all or any part of
            your outstanding Account balance, without penalty. Payment of more
            than the payment due in one billing cycle will not relieve you of
            the obligation to pay the entire payment due in subsequent billing
            cycles.
          </p>

          <h4>7. Fees</h4>
          <p>
            A. The Fees applicable to your Account are described above. You are
            responsible for Fees in addition to Charges.
          </p>
          <p>
            B. Returned Payment fees become payable by you each time a payment
            on your Account is returned or reversed for any reason or we must
            return any check, instrument, or transaction you send us because it
            is incomplete or faulty, we will charge you a returned payment fee
            of $29.00. However, we will not charge a returned payment fee if we
            are charging a late fee with respect to the same minimum payment due
            and will never charge a returned payment fee exceeding the minimum
            payment due that was due immediately prior to the date on which the
            payment was returned to us.
          </p>
          <p>
            C. Foreign Currency Transactions. If you make a transaction using
            your Account in a foreign currency (including, for example, online
            purchases from a merchant located outside of the U.S.), the credit
            card association will convert any transaction in foreign currency
            into U.S. dollars using an exchange rate for the applicable central
            processing date that is (1) selected by the association from the
            range of rates available in wholesale currency markets, which rate
            may vary from the rate the association receives, or (2) the
            government mandated rate. The conversion rate you get may differ
            from the rate on the transaction date or the posting date, and from
            the rate that the credit card association gets. A merchant or other
            third party may convert a transaction into U.S. dollars or another
            currency, using a rate they select, before sending it to the credit
            card association.
          </p>
          <p>
            D. Additional Services. We may, from time to time, make additional
            services available to you directly or through affiliated or
            non-affiliated third parties. Without limitation, such services may
            include providing you with virtual accounts in order to enable you
            to use your Account or otherwise engage in various financial
            transactions that we do not directly provide. Each such service is
            subject to its own terms and conditions, and we shall not be
            responsible to you for any aspect of those services. We may permit
            you to charge any applicable fees for such services to your Account.
            You acknowledge and agree that we may receive compensation or
            otherwise benefit as a result of making such services available to
            you.
          </p>

          <h4>8. Managing Your STBL Cards</h4>
          <h4>8.1 Requesting and Replacing Cards</h4>
          <p>
            We or STBL may decide not to grant requests for STBL Cards or limit
            the number of physical or virtual STBL Cards provided to you.
          </p>
          <p>
            You are responsible for securing STBL Cards, account numbers, and
            STBL Card security features. You will promptly notify us and take
            appropriate measures to prevent unauthorized transactions when a
            STBL Card is lost, stolen, breached, or needs to be replaced. In
            such cases, you may request the issuance of replacement STBL Cards
            through your STBL Account. Replacement Cards may have new account
            numbers that could require you to update the STBL Card on file for
            any scheduled or recurring payments. You are solely responsible for
            updating STBL Card information stored with merchants where account
            numbers have been changed.
          </p>

          <h4>8.2 Permitted and Unauthorized Use</h4>
          <p>
            You agree to establish and maintain controls designed to ensure that
            the STBL Cards are only used by you and your permitted authorized
            users for bona fide purposes and in compliance with these Card
            Terms, any Issuer terms, and applicable law. You are responsible for
            Charges and transactions made by any person given access to STBL
            Cards even if they are not the person associated with or named on
            the STBL Card.
          </p>
          <p>
            STBL, Issuer, Card Networks, or other intermediary third-party
            service providers (including merchant acquirers) may deny or reverse
            Charges for any reason. Issuer is not responsible for any losses,
            damages, or harm caused by any Charges that are denied or reversed.
          </p>

          <h4>8.3 Lost or Stolen Cards</h4>
          <p>
            If your Card is lost or stolen or if you think someone may be using
            your Card or Account without your permission, you must notify us
            promptly by emailing us at{" "}
            <a href="mailto:support@stbl.io">support@stbl.io</a> or calling us
            at +1 (385) 600-7758. You will not be liable for any unauthorized
            use that occurs after you notify us. You may, however, be liable for
            unauthorized use that occurs before receipt of your notice by us.
            You have an obligation to assist us in our investigation if your
            STBL Card is lost or stolen or you believe someone is using your
            STBL Card or your STBL Account without your permission.
          </p>

          <h4>9. Chargebacks</h4>
          <p>
            You are responsible for reviewing your Periodic Statements promptly
            and identifying any Charges that you believe are unauthorized or
            that you dispute.
          </p>
          <p>
            If you and a merchant have a dispute regarding a Charge identified
            on your Periodic Statement, such as delivery of incorrect goods or
            services or being charged the wrong amount, you should first attempt
            to resolve the dispute with the merchant. If the dispute is not
            resolved to your satisfaction or if you believe the Charge is
            unauthorized, you may initiate a Chargeback through your STBL
            Account. You must report any disputed Charge or error no more than
            60 days after the disputed Charge is posted on your Periodic
            Statement. We may require additional details on the transaction and
            our review of your disputed Charge will be conditioned on you
            providing all of the information we may require to review the
            disputed Charge.
          </p>
          <p>
            You understand that the STBL Card is subject to Card Network rules
            regarding chargebacks. The Card Networks have additional established
            procedures for resolving chargebacks that may require you to provide
            further details of the disputed Charge or associated documentation.
          </p>
          <p>
            Charges relating to disputed Charges and Chargebacks that are
            pending resolution may still be due and owing as of the date that
            payment is due as provided in the applicable Periodic Statement.
            Chargebacks resolved in your favor will be credited to your STBL
            Account on either the current or a future Periodic Statement. We may
            impose Fees, reduce your spending limits, or suspend access to your
            STBL Account or the Services if you fail to pay Charges relating to
            Chargebacks that are pending resolution on the payment date.
          </p>

          <h4>10. Termination</h4>
          <p>
            Subject to applicable law, we may suspend, revoke or cancel your
            Account privileges, your right to use the Card or deny any
            transaction, in our sole discretion at any time, with or without
            cause and with or without giving you notice. Any termination of
            credit privileges, whether initiated by us or by you, will not
            affect any of our rights or your obligations under these Card Terms,
            including your obligation to repay any amounts you owe us according
            to the terms of these Card Terms. On our demand or upon termination
            of credit privileges, you agree to surrender to us or destroy the
            Card. If you attempt to use the Card after the termination of credit
            privileges (whether or not we have provided notice of such
            termination), the Card may be retained by a merchant, ATM or
            financial institution where you attempt to use the Card.
          </p>

          <h4>11. Change of Terms</h4>
          <p>
            Subject to applicable law, we may at any time change, add to or
            delete terms and conditions of these Card Terms, including interest
            rates and this Change of Terms provision. Such changes may be based
            on our anti-fraud policies and procedures, your level of compliance
            with these Card Terms, prevailing economic conditions and/or any
            other factors. We will give you notice of any change, addition or
            deletion as required by applicable law. As of the effective date, the
            changed terms, at our option, will apply to new purchases and the
            outstanding balances of your Account, to the extent permitted by
            applicable law.
          </p>

          <h4>12. Default and Remedies</h4>
          <p>We may consider you in default of these Card Terms if:</p>
          <ul>
            <li>You do not make any payment when it is due;</li>
            <li>
              Any payment you make is rejected, not paid or cannot be processed;
            </li>
            <li>You exceed a credit limit;</li>
            <li>
              A bankruptcy or other insolvency proceeding is filed by or against
              you;
            </li>
            <li>
              We determine that you made a false, incomplete or misleading
              statement on any of your Account documentation, or you otherwise
              tried to defraud us;
            </li>
            <li>
              We reasonably believe that you are or may become unable to pay all
              of your financial obligations; or
            </li>
            <li>
              You do not comply with any term of these Card Terms or any other
              agreement with us.
            </li>
          </ul>
          <p>
            In the event of your default under these Card Terms, we may, subject
            to applicable law (including any applicable notice requirement): (a)
            declare all or any portion of your outstanding Account balance to be
            immediately due and payable; (b) instead allow you to repay your
            Account balance by paying the minimum payment due each billing
            cycle, without waiving any rights under subsection (a); and/or (c)
            commence a collection action against you and charge you for any
            court costs and/or any reasonable attorneys&rsquo; fees and costs we
            are charged in connection with such action by any attorney who is
            not our salaried employee. After a default, interest charges will
            continue to accrue until your total Account balance, including
            accrued interest charges, is paid in full, subject to applicable
            law.
          </p>

          <h4>13. Delay in Enforcement</h4>
          <p>
            We may at any time and in our sole discretion delay or waive
            enforcing any of our rights or remedies under these Card Terms or
            under applicable law without losing any of those or any other rights
            or remedies. Even if we do not enforce our rights or remedies at any
            one time, we may enforce them at a later date. For example, we may
            accept late payments without losing any of our rights under these
            Card Terms.
          </p>

          <h4>14. Communications and Call Recording</h4>
          <p>
            You authorize STBL, Issuer and their partners (each of Issuer&rsquo;s
            affiliates, agents, assigns, and service providers (collectively,
            the &ldquo;Messaging Parties&rdquo;) to use automatic telephone
            dialing systems, artificial or prerecorded voice message systems,
            text messaging systems and automated email systems, or any system
            capable of storing and dialing telephone numbers to deliver messages
            relating to these Card Terms, your Account, or your relationship
            with the Messaging Parties more generally (including but not limited
            to: messages about upcoming payment due dates, missed payments and
            returned payments) to any telephone number(s) you provide to the
            Messaging Parties. You also agree that these messages may deliver
            prerecorded and/or artificial voice messages. You understand that
            telephone messages may be played by a machine automatically when the
            telephone is answered, whether answered by you or someone else, and
            that these messages may also be recorded by your answering machine.
            You also authorize the Messaging Parties to deliver messages to you
            via mail or email at any addresses you supply to them or that they
            obtain through any legal means.
          </p>
          <p>
            By accepting these Card Terms or using your Card, you acknowledge
            that you have received, reviewed, and agree to be bound by the
            Issuer&rsquo;s E-Sign &amp; Electronic Communications Notice (the
            &ldquo;E-Sign Notice&rdquo;), which is incorporated herein by
            reference. You consent to receive all disclosures, notices,
            agreements, and other communications from Issuer and the Messaging
            Parties in electronic form, in accordance with the E-Sign Notice.
            You agree that such electronic communications satisfy any legal
            requirement that such communications be in writing. The E-Sign
            Notice may be amended by Issuer from time to time, and your
            continued use of the Card after any such amendment constitutes your
            acceptance of the amended E-Sign Notice.
          </p>
          <p>
            You understand that anyone with access to your mail, telephone or
            email account may listen to or read the messages the Messaging
            Parties leave or send you, and you agree that the Messaging Parties
            will have no liability for anyone accessing such messages. You
            further understand that, when you receive a telephone call, text
            message or email, you may incur a charge from the company that
            provides you with telecommunications, wireless and/or internet
            services, and you agree that the Messaging Parties will have no
            liability for such charges except to the extent required by
            applicable law. You expressly authorize the Messaging Parties to
            monitor and record your calls with the Messaging Parties. If any
            telephone number you have provided to the Messaging Parties changes,
            or if you cease to be the owner, subscriber, or primary user of any
            such telephone number, you agree to immediately give notice to the
            Messaging Party who delivered the messages of such facts so that the
            Messaging Party can update its records.
          </p>
          <p>
            This authorization is part of our bargain concerning these Card
            Terms, and we do not intend it to be revocable. However, to the
            extent you have the right to revoke your consent to communications
            by autodialed calls and text messages to your mobile number under
            applicable law, you may exercise this right by contacting the
            applicable Messaging Party directly or by sending a request by email
            to <a href="mailto:support@stbl.io">support@stbl.io</a> with the
            subject line &ldquo;END COMMUNICATIONS.&rdquo; You may opt-out of
            receiving most of these messages at any time by sending us a request
            to <a href="mailto:support@stbl.io">support@stbl.io</a> or by
            responding &ldquo;STOP&rdquo; to any text message. To stop emails
            only, you can follow the opt-out instructions included at the bottom
            of the Messaging Parties&rsquo; emails.
          </p>

          <h4>15. Governing Law</h4>
          <p>
            These Card Terms will be interpreted in accordance with the laws of
            Puerto Rico without regard to conflict-of-law provisions. Judicial
            proceedings (other than small claims actions) that are excluded from
            the Arbitration section below must be brought in state or federal
            court in Puerto Rico, unless we both agree in writing to some other
            location, and you hereby consent to the venue and personal
            jurisdiction of such court.
          </p>

          <h4>16. Dispute Resolution &amp; Arbitration</h4>
          <div className={styles.importantNote}>
            <p>
              PLEASE READ THIS &ldquo;DISPUTE RESOLUTION AND ARBITRATION&rdquo;
              PROVISION VERY CAREFULLY. IT LIMITS YOUR RIGHTS IN THE EVENT OF A
              DISPUTE BETWEEN YOU AND ISSUER. STBL IS THE TECHNOLOGY PROVIDER
              FOR THE CARD BUT IS NOT THE ISSUER, CREDITOR OR LENDER. STBL IS
              NOT A PARTY TO THESE CARD TERMS AND HAS NO LIABILITY FOR THE
              ISSUER&rsquo;S OBLIGATIONS UNDER IT. HOWEVER, STBL IS AN EXPRESS
              THIRD-PARTY BENEFICIARY OF THESE CARD TERMS WITH RESPECT TO ANY
              PROVISIONS THAT ALLOCATE RISK, DISCLAIM LIABILITY, LIMIT REMEDIES
              OR REQUIRE DISPUTES TO BE RESOLVED THROUGH ARBITRATION.
              ACCORDINGLY, TO THE EXTENT A DISPUTE INVOLVES STBL, STBL SHALL BE
              ENTITLED TO INVOKE AND BENEFIT FROM THE SAME PROTECTIONS,
              LIMITATIONS AND DISPUTE RESOLUTION PROCEDURES AS THE ISSUER. TO
              THE EXTENT YOU HAVE ANY DISPUTE YOU MAY HAVE WITH STBL RELATING
              SOLELY TO SERVICES PROVIDED TO YOU UNDER YOUR SEPARATE USER TERMS
              WITH STBL, SUCH DISPUTES WILL BE GOVERNED EXCLUSIVELY IN
              ACCORDANCE WITH THE STBL USER TERMS.
            </p>
          </div>

          <h4>Binding Arbitration</h4>
          <p>
            (a) You and Issuer agree that any and all past, present and future
            Disputes (defined below) shall be determined by arbitration, unless
            your Dispute is subject to an exception to this agreement to
            arbitrate set forth below. You and Issuer further agree that any
            arbitration pursuant to this section shall not proceed as a class,
            group or representative action. The award of the arbitrator may be
            entered in any court having jurisdiction. &ldquo;Dispute&rdquo;
            means any dispute, claim, or controversy between you and Issuer that
            arises out of or relates to (i) these Card Terms (including any
            addenda hereto or other terms incorporated herein by reference),
            (ii) the breach, termination, enforcement, interpretation or
            validity hereof, including the determination of the scope or
            applicability of the agreement to arbitrate hereunder, or (iii) any
            Services (including, without limitation, the STBL Card).
          </p>
          <p>
            (b) This agreement to arbitrate shall be construed under and be
            subject to the Federal Arbitration Act, notwithstanding any other
            choice of law set out in these Card Terms.
          </p>

          <h4>Arbitration Procedure</h4>
          <p>
            (a) Before filing a claim against Issuer, you agree to try to
            resolve the Dispute informally by providing written notice to Issuer
            of the actual or potential Dispute. Similarly, Issuer will provide
            written notice to you of any actual or potential Dispute to endeavor
            to resolve any claim we may possess informally before taking any
            formal action. The party that provides the notice of the actual or
            potential Dispute (the &ldquo;Notifying Party&rdquo;) will include
            in that notice (a &ldquo;Notice of Dispute&rdquo;) your name, the
            Notifying Party&rsquo;s contact information for any communications
            relating to such Dispute, and sufficient details regarding such
            Dispute to enable the other party (the &ldquo;Notified Party&rdquo;)
            to understand the basis of and evaluate the concerns raised. If the
            Notified Party responds within ten (10) business days after
            receiving the Notice of Dispute that it is ready and willing to
            engage in good faith discussions in an effort to resolve the Dispute
            informally, then each party shall promptly participate in such
            discussions in good faith.
          </p>
          <p>
            (b) If, notwithstanding the Notifying Party&rsquo;s compliance with
            all of its obligations under the preceding paragraph, a Dispute is
            not resolved within thirty (30) days after the Notice of Dispute is
            sent (or if the Notified Party fails to respond to the Notice of
            Dispute within ten (10) business days), the Notifying Party may
            initiate an arbitration proceeding as described below. If either
            party purports to initiate arbitration without first providing a
            Notice of Dispute and otherwise complying with all of its
            obligations under the preceding paragraph, then, notwithstanding any
            other provision of these Card Terms, the arbitrator(s) will promptly
            dismiss the claim with prejudice and will award the other party all
            of its costs and expenses (including, without limitation, reasonable
            attorneys&rsquo; fees) incurred in connection with such Dispute.
          </p>
          <p>
            (c) You and Issuer each agree to resolve any Disputes that are not
            resolved informally as described above through final and binding
            arbitration as discussed herein, except as set forth under
            &ldquo;Small Claims&rdquo; below. You and Issuer agree that the
            American Arbitration Association (&ldquo;AAA&rdquo;) will administer
            the arbitration under its Consumer Arbitration Rules (the
            &ldquo;Rules&rdquo;). The Rules are available at{" "}
            <a
              href="https://www.adr.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.adr.org
            </a>{" "}
            or by calling the AAA at 1-800-778-7879. A party who desires to
            initiate arbitration must provide the other party with a written
            Demand for Arbitration as specified in the AAA Rules. (The AAA
            provides a general Demand for Arbitration.) Arbitration will proceed
            on an individual basis and will be handled by a sole arbitrator. The
            single arbitrator will be either a retired judge or an attorney
            licensed to practice law and will be selected by the parties from
            the AAA&rsquo;s roster of arbitrators. If the parties are unable to
            agree upon an arbitrator within fourteen (14) days of delivery of
            the Demand for Arbitration, then the AAA will appoint the arbitrator
            in accordance with the AAA Rules. The arbitrator(s) shall be
            authorized to award any remedies, including injunctive relief, that
            would be available to you in an individual lawsuit, subject to any
            effective and enforceable limitations of liability or exclusions of
            remedies set forth herein. Notwithstanding any language to the
            contrary in this paragraph, if a party seeks injunctive relief that
            would significantly impact other Issuer users as reasonably
            determined by either party, the parties agree that such arbitration
            will proceed on an individual basis but will be handled by a panel
            of three (3) arbitrators. Each party shall select one arbitrator,
            and the two party-selected arbitrators shall select the third, who
            shall serve as chair of the arbitral panel. That chairperson shall
            be a retired judge or an attorney licensed to practice law and with
            experience arbitrating or mediating disputes. In the event of
            disagreement as to whether the threshold for a three-arbitrator
            panel has been met, the sole arbitrator appointed in accordance with
            this Section shall make that determination. If the arbitrator
            determines a three-person panel is appropriate, the arbitrator may
            -- if selected by either party or as the chair by the two
            party-selected arbitrators -- participate in the arbitral panel.
            Except as and to the extent otherwise may be required by law, the
            arbitration proceeding and any award shall be confidential.
          </p>
          <p>
            (d) You and Issuer further agree that the arbitration will be held
            in the English language in New York, New York, or, if you so elect,
            all proceedings can be conducted via videoconference, telephonically
            or via other remote electronic means.
          </p>
          <p>
            (e) Filing costs and administrative fees shall be paid in accordance
            with the AAA Rules; provided that the prevailing party will be
            entitled to recover its reasonable attorneys&rsquo; fees, expert
            witness fees, and out-of-pocket costs incurred in connection with
            the arbitration proceeding, in addition to any other relief it may
            be awarded.
          </p>
          <p>
            (f) You and Issuer agree that, notwithstanding anything to the
            contrary in the Rules, the arbitration of any Dispute shall proceed
            on an individual basis, and neither you nor Issuer may bring a claim
            as a part of a class, group, collective, coordinated, consolidated
            or mass arbitration (each, a &ldquo;Collective Arbitration&rdquo;).
            Without limiting the generality of the foregoing, a claim to resolve
            any Dispute against Issuer will be deemed a Collective Arbitration
            if (i) two (2) or more similar claims for arbitration are filed
            concurrently by or on behalf of one or more claimants; and (ii)
            counsel for the claimants are the same, share fees or coordinate
            across the arbitrations. &ldquo;Concurrently&rdquo; for purposes of
            this provision means that both arbitrations are pending (filed but
            not yet resolved) at the same time.
          </p>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NEITHER YOU NOR
            ISSUER SHALL BE ENTITLED TO CONSOLIDATE, JOIN OR COORDINATE DISPUTES
            BY OR AGAINST OTHER INDIVIDUALS OR ENTITIES, OR ARBITRATE OR
            LITIGATE ANY DISPUTE IN A REPRESENTATIVE CAPACITY, INCLUDING AS A
            REPRESENTATIVE MEMBER OF A CLASS OR IN A PRIVATE ATTORNEY GENERAL
            CAPACITY. IN CONNECTION WITH ANY DISPUTE, ANY AND ALL SUCH RIGHTS
            ARE HEREBY EXPRESSLY AND UNCONDITIONALLY WAIVED. Without limiting
            the foregoing, any challenge to the validity of this paragraph shall
            be determined exclusively by the arbitrator.
          </p>

          <h4>Small Claims</h4>
          <p>
            Notwithstanding your and Issuer&rsquo;s agreement to arbitrate
            Disputes, You and Issuer retain the right to bring an individual
            action in small claims court.
          </p>

          <h4>Class Waiver</h4>
          <p>
            To the extent applicable law permits, any dispute arising out of or
            relating to these Card Terms, whether in arbitration or in court,
            will be conducted only on an individual basis and not in a class,
            consolidated or representative action. Notwithstanding any other
            provision of these Card Terms or the AAA Rules, disputes regarding
            the interpretation, applicability, or enforceability of this class
            waiver may be resolved only by a court and not by an arbitrator. If
            this waiver of class or consolidated actions is deemed invalid or
            unenforceable, neither party is entitled to arbitration.
          </p>

          <h4>No Jury Trial</h4>
          <p>
            If for any reason a claim or dispute proceeds in court rather than
            through arbitration, each party knowingly and irrevocably waives any
            right to trial by jury in any action, proceeding or counterclaim
            arising out of or relating to these Card Terms.
          </p>

          <h4>Venue and Jurisdiction for Judicial Proceedings</h4>
          <p>
            Except as otherwise required by applicable law or provided in these
            Card Terms, in the event that the agreement to arbitrate is found
            not to apply to you or your Dispute, you and Issuer agree that any
            judicial proceeding may only be brought in a court of competent
            jurisdiction in Puerto Rico. Both you and Issuer irrevocably consent
            to venue and personal jurisdiction in Puerto Rico; provided that
            either party may bring any action to confirm an arbitral award in
            any court having jurisdiction.
          </p>

          <h4>Confidentiality</h4>
          <p>
            The existence of all information regarding any Dispute will be held
            in strict confidence by the parties and will not be disclosed by
            either party except as reasonably necessary in connection with the
            conduct of the arbitration or the confirmation or enforcement of any
            arbitral award. Any such permitted disclosure will, to the maximum
            extent reasonably practicable, be made subject to obligations of
            confidentiality at least as stringent as the provisions of this
            paragraph. If any disclosure of information regarding any Dispute is
            required under applicable law, the parties shall reasonably
            cooperate with one another to obtain protective orders or otherwise
            to preserve the confidentiality of such information.
          </p>

          <h4>17. Survival</h4>
          <p>
            The following provisions of these Card Terms shall survive any
            termination or expiration of these Card Terms and shall remain in
            full force and effect until all of your obligations to us have been
            fully and finally satisfied: (a) all of your payment obligations and
            our right to collect all amounts owed by you; (b) all security
            interest, collateral, and setoff provisions, including our rights in
            any Collateral; (c) any indemnification obligations; (d) the
            limitation of liability provisions; (e) the disclaimer of warranties
            provisions; (f) the dispute resolution and arbitration provisions,
            including the class waiver; (g) our communications and contact
            rights; (h) our assignment rights; (k) any waiver provisions; (l)
            all representations and warranties made by you; (m) any accrued
            rights, remedies, or causes of action in favor of either party; and
            (n) any other provision that by its nature or express terms is
            intended to survive.
          </p>

          <h4>18. Severability</h4>
          <p>
            If any provision of these Card Terms is found to be invalid, illegal,
            or unenforceable by a court or arbitrator of competent jurisdiction,
            such provision shall be modified to the minimum extent necessary to
            make it valid and enforceable while preserving its intent, or if
            such modification is not possible, such provision shall be severed
            from these Card Terms. The remaining provisions shall remain in full
            force and effect.
          </p>
          <p>
            Notwithstanding the foregoing paragraph, in the event that a court
            finds the prohibition of Collective Arbitration in Section 16 to be
            invalid or unenforceable, then all provisions in Section 16 shall be
            deemed void, except for any portion of any provision in Section 16
            related to the resolution of Disputes through litigation in court.
          </p>

          <h4>19. Assignment</h4>
          <p>
            These Card Terms will be binding on, and benefit, any of your and
            our successors and assigns. You may not transfer your Account or
            your Agreement to someone else without our written permission. We
            may transfer your Account, these Card Terms, or any of our rights or
            obligations therein, to another company or person at any time,
            without your permission and without prior notice to you. If we do,
            they will take our place under these Card Terms. You must pay them
            and perform all of your obligations to them and not us. If you pay
            us after you are informed or learn that we have transferred your
            Account or this Agreement, we can handle your payment in any way we
            think is reasonable. This includes returning the payment to you or
            forwarding the payment to the other company or person.
          </p>

          <h4>20. Entire Agreement</h4>
          <p>
            These Card Terms constitute the entire agreement between you and
            Issuer regarding the subject matter hereof and supersede all prior
            or contemporaneous agreements, understandings, representations, and
            communications whether written or oral, regarding such subject
            matter.
          </p>

          <h4>21. Force Majeure</h4>
          <p>
            We shall not be liable for any delay or failure to perform any
            obligation under these Card Terms to the extent that such delay or
            failure is caused by circumstances beyond our reasonable control,
            including but not limited to acts of God, natural disasters, war,
            terrorism, civil unrest, labor disputes, government actions,
            epidemics or pandemics, internet or telecommunications failures, or
            failures of third-party service providers.
          </p>

          <h4>22. Disclaimer of Warranties</h4>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, WE EXPRESSLY
            DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            TITLE, NON-INFRINGEMENT, ACCURACY, RELIABILITY, AND ANY WARRANTIES
            ARISING FROM COURSE OF DEALING OR USAGE OF TRADE.
          </p>

          <h4>23. Limitation of Liability</h4>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
            ISSUER, ITS AFFILIATES, OR ITS OFFICERS, DIRECTORS, EMPLOYEES,
            AGENTS, SERVICE PROVIDERS, OR LICENSORS BE LIABLE TO YOU FOR ANY
            INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, EXEMPLARY, PUNITIVE,
            OR OTHER SIMILAR DAMAGES, INCLUDING BUT NOT LIMITED TO LOST PROFITS,
            LOST REVENUE, LOST DATA, LOSS OF GOODWILL, BUSINESS INTERRUPTION, OR
            LOSS OF BUSINESS OPPORTUNITY, WHETHER ARISING IN CONTRACT, TORT,
            NEGLIGENCE, STRICT LIABILITY, OR OTHERWISE.
          </p>
          <p>
            IN NO EVENT SHALL ISSUER&rsquo;S TOTAL LIABILITY TO YOU FOR ALL
            CLAIMS ARISING OUT OF OR RELATING TO THIS AGREEMENT OR YOUR ACCOUNT
            EXCEED THE LESSER OF (A) YOUR ACTUAL DIRECT DAMAGES PROVEN OR (B)
            THE TOTAL AMOUNTS PAID BY YOU IN THE TWELVE (12) MONTHS IMMEDIATELY
            PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
          </p>

          <h4>24. Indemnity</h4>
          <p>
            You will indemnify and defend Issuer, its affiliates, and its
            partners, directors, officers, employees, agents, trustees,
            administrators, managers, advisors, and representatives (each an
            &ldquo;Indemnitee&rdquo;) against, and hold each Indemnitee harmless
            from, any and all claims, litigation, investigations, proceedings,
            losses, damages, fines, penalties, liabilities, settlements, costs,
            fees, and expenses incurred by any Indemnitee or asserted against
            any Indemnitee by any person arising out of, in connection with,
            related to, or as a result of your (i) breach of any of the
            representations, warranties, or covenants contained in these Card
            Terms; or (ii) gross negligence, fraud, or violation of any
            applicable law or rights of any third-party. Issuer may defend any
            claim subject to indemnification hereunder, using counsel of its
            choice, and you will pay or promptly reimburse Issuer for the
            reasonable fees of such counsel and all related costs and reasonable
            expenses. If you are a user from a jurisdiction that does not allow
            certain indemnification obligations, you agree that this indemnity
            is intended to be as broad as permitted under the laws of such
            jurisdiction.
          </p>
        </section>
      </div>
    </LegalLayout>
  );
};

export default CardTermsInternational;
